import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';

/** Fired when this screen has marked the history read. `shell.js` listens. */
export const SEEN = 'cueforge:notifications-seen';

/**
 * What you were told while you were not looking — GitHub issue #49.
 *
 * WHY A LIST EXISTS AT ALL. A push notification appears once and is gone:
 * swiped away in a pocket, cleared by a reboot, or never shown because
 * permission was declined or the phone was off. Everything this app pushes is
 * worth reading late — somebody spoke in your circle, a venue put a notice
 * up, something is waiting on your approval — and until this there was
 * nowhere to read it late. The server records what it SENT rather than what
 * was delivered, so the person who never granted permission has the fullest
 * list rather than an empty one.
 *
 * OPENING IT IS READING IT. The count clears on arrival rather than per row:
 * a per-item read state would need somewhere to put it and a rule for what
 * "read" means on a list you scrolled past, and neither earns its keep on a
 * screen whose whole job is "what did I miss".
 */
export function NotificationsScreen({ onDone, onGo }) {
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState('');

  useEffect(() => {
    let alive = true;
    get('/api/notifications')
      .then((n) => {
        if (!alive) return;
        setState(n);
        // Marked after the list is IN HAND, not before: a failed read that
        // had already cleared the count would lose the only pointer to what
        // was missed.
        return post('/api/notifications/seen');
      })
      .then(() => {
        // TELL THE CHROME. The bell reads the count when it mounts and when
        // the screen changes, and both of those happen BEFORE this request
        // finishes — so without an announcement the bell went on showing a
        // count for something you were looking at, until the minute-long
        // poll came round. An event rather than shared state because the
        // shell and this screen are siblings, and threading a setter down
        // through the router to reach one of them is a lot of wiring for one
        // number.
        window.dispatchEvent(new CustomEvent(SEEN));
      })
      .catch((e) => { if (alive) setFailed(e.detail || e.message); });
    return () => { alive = false; };
  }, []);

  const items = (state && state.items) || [];

  return html`
    <div class="screen" data-testid="notifications">
      <button class="secondary" data-testid="notifications-back"
              onClick=${onDone}>Back</button>
      <h2>Notifications</h2>

      ${failed && html`
        <p class="sub" style="color:var(--danger)"
           data-testid="notifications-error">${failed}</p>`}

      ${state === null && !failed
        ? html`<p class="sub">Loading…</p>`
        : items.length === 0
          ? html`<p class="empty" data-testid="notifications-empty">
                   Nothing yet. When somebody posts in a circle you are in, or
                   a venue puts a notice up, it will be here.
                 </p>`
          : items.map((n, i) => html`
            ${/* Keyed by timestamp AND index: two notifications can share a
                  millisecond when one event tells a whole circle, and a
                  duplicate key silently drops a row. */''}
            <button key=${`${n.ts}-${i}`} class="secondary card rowbetween"
                    data-testid=${`notification-${i}`}
                    onClick=${() => go(n.url, onGo, onDone)}>
              <span>
                <span style="display:block">${n.title}</span>
                <span class="sub" style="text-transform:none">${n.body}</span>
              </span>
              <span class="sub mono">${when(n.ts)}</span>
            </button>`)}
    </div>`;
}

/**
 * A notification carries the URL it was pushed with, which is a hash route
 * like `/#/venues/abc`. Handing that to the router directly would mean
 * parsing it back into a screen and its arguments here; setting the hash lets
 * the app's own listener do the job it already does.
 */
function go(url, onGo, onDone) {
  if (!url || url === '/') { onDone(); return; }
  const hash = url.indexOf('#');
  if (hash === -1) { onDone(); return; }
  window.location.hash = url.slice(hash + 1);
}

/**
 * How long ago, in the fewest characters that are still true.
 *
 * Relative rather than a clock time, because the question this list answers
 * is "did I miss this" and not "at what time". Anything older than a week
 * gets a date — "37d" is arithmetic, not information.
 */
function when(ts) {
  const then = Date.parse(ts);
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(then).toLocaleDateString(undefined,
                                           { day: 'numeric', month: 'short' });
}
